/**
 * Load Published Meeting utility - Loads published meeting from backend
 * Fetches sections and converts them to Redux assets and timeline items
 * Similar to loadDraft but for published meetings (uses Section.order instead of draftData.sectionIds)
 */

import MeetingService from '../../../services/MeetingService';
import { convertSectionsToTimeline } from '../../CreateVideoNewPage/lib/convertSections';

/**
 * Loads a published meeting from the backend
 * @param {Object} params - Load meeting parameters
 * @param {string} params.meetingId - Meeting ID
 * @param {string} params.meetingVersion - Optional version number
 * @param {Function} params.onProgress - Progress callback (step, totalSteps, progress)
 * @returns {Promise<Object>} - { meeting, assets, timelineItems }
 */
export async function loadPublishedMeeting({ meetingId, meetingVersion, onProgress }) {
  if (!meetingId) {
    throw new Error('Meeting ID is required');
  }

  if (onProgress) {
    onProgress(0, 3, 0);
  }

  // Step 1: Get meeting (includes Sections with Question.Answers)
  const meeting = await MeetingService.getMeeting(meetingId, meetingVersion);

  if (onProgress) {
    onProgress(1, 3, 33);
  }

  // Step 2: Use sections from meeting (includes Question.Answers)
  // Backend returns sections sorted by Section.order ASC
  const sections = meeting?.Sections || [];

  if (!sections || sections.length === 0) {
    // No sections found
    return {
      meeting,
      assets: [],
      timelineItems: [],
    };
  }

  if (onProgress) {
    onProgress(2, 3, 66);
  }

  // Step 3: Convert sections to assets and timeline items
  // Backend already returns sections sorted by Section.order, so no need to sort
  const { assets, timelineItems } = convertSectionsToTimeline(sections);

  if (onProgress) {
    onProgress(3, 3, 100);
  }

  return {
    meeting,
    assets,
    timelineItems,
  };
}

